import type { TableProps as MantineTableProps } from '@mantine/core';
import { Loader, Table as MantineTable } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { IconArrowNarrowDown, IconArrowNarrowUp } from '@tabler/icons-react';

import dt from '~/testing';
import { Title } from '~/domains/design-system/Title';

import { TablePagination } from './TablePagination';
import { TableExportButtons } from './TableExportButtons';
import { useStyles } from './styles';

export type TableProps = MantineTableProps & {
  columnHeaders: {
    label: string;
    key: string;
    sort?: 'ASC' | 'DESC';
    onClick?: () => void;
    style?: React.CSSProperties;
  }[];
  rows:
    | {
        [key: string]: React.ReactNode;
      }[]
    | {
        _rowClick?: () => void;
        _rowHover?: () => void;
        _rowHoverEnd?: () => void;
        id?: string;
      }[];
  rowStyles?: Record<number, React.CSSProperties>;
  rowClassNames?: Record<number, string>;
  cellStyles?: Record<number, Record<number, React.CSSProperties>>;
  pagination?: {
    total: number;
    offset: number;
    limit: number;
    scrollToTop?: boolean;
    setLimit: (limit: number) => void;
    setOffset: (offset: number) => void;
    unit?: string;
    pageSizes?: string[];
  };
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  emptyStyle?: React.CSSProperties;
  /** Show the "Export page (.csv)" footer buttons */
  showExportButtons?: boolean;
  lastRowRef?: (node: HTMLTableRowElement | null) => void;
};

function Table({
  className,
  columnHeaders,
  rows = [],
  rowStyles,
  rowClassNames,
  cellStyles,
  pagination,
  isLoading = true,
  emptyState,
  emptyStyle,
  showExportButtons = true,
  lastRowRef,
  ...rest
}: TableProps) {
  const { t } = useTranslation('common');
  const { classes, cx } = useStyles();

  return (
    <>
      <MantineTable {...rest} bg="white" className={cx(classes.table, className)}>
        <thead>
          <tr>
            {columnHeaders?.map(({ label, key, sort, onClick, style }) => (
              <th
                key={key}
                className={`${classes.column} ${onClick ? classes.sortableColumn : ''}`}
                onClick={onClick}
                style={style}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: /* eslint-disable-line local-rules/use-theme-tokens */ '4px' }}>
                  <Title variant="subhead-x-small">{label.toUpperCase()}</Title>
                  {sort === 'ASC' && <IconArrowNarrowUp size={16} />}
                  {sort === 'DESC' && <IconArrowNarrowDown size={16} />}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ marginBottom: /* eslint-disable-line local-rules/use-theme-tokens */ pagination ? 32 : 16 }}>
          {isLoading && (
            <tr>
              <td
                colSpan={columnHeaders.length}
                style={emptyStyle ?? { textAlign: 'center', height: '50vh' }}
              >
                <Loader />
              </td>
            </tr>
          )}
          {!isLoading && rows.length === 0 && (
            <tr>
              <td
                colSpan={columnHeaders.length}
                style={emptyStyle ?? { textAlign: 'center', height: '50vh' }}
              >
                {emptyState ?? t('table.noData')}
              </td>
            </tr>
          )}
          {!isLoading &&
            rows.length > 0 &&
            rows.map((row, index) => (
              <tr
                ref={index === rows.length - 1 ? lastRowRef : undefined}
                data-test-id={dt.components.shared.table.tableRow}
                // eslint-disable-next-line react/no-array-index-key
                key={`row-${index}`}
                id={`table-row-${row.id}`}
                className={rowClassNames?.[index]}
                style={rowStyles?.[index]}
                onClick={row._rowClick}
                onMouseEnter={row._rowHover}
                onMouseLeave={row._rowHoverEnd}
                onFocus={row._rowHover}
              >
                {columnHeaders.map(({ key }, cellIndex) => (
                  <td
                    data-test-id={dt.components.shared.table.tableCell}
                    data-column-key={key}
                    key={key}
                    className={classes.column}
                    style={cellStyles?.[index]?.[cellIndex]}
                  >
                    {row[key]}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </MantineTable>
      {!isLoading && rows.length > 0 && (
        <div className={classes.tableFooter}>
          {pagination && <TablePagination {...pagination} />}
          {showExportButtons && (
            <TableExportButtons showExportAll={!!pagination} variant="inline" />
          )}
        </div>
      )}
    </>
  );
}

export { Table };
